import { Button, List, Tabs, Tooltip } from 'antd';
import { ReactNode, UIEventHandler, useCallback } from 'react';
import ActionIcon from '@/components/_common/ActionIcon';
import './style.less';
import { Group } from '@/components/_common';
import { FileText, MessageSquare, Volume, VolumeX } from 'react-feather';
import { useUserNotificationMarkAllAsRead } from '@/core/domain/communication/userNotification/userNotification.hooks';

interface Props {
  dataSource?: {
    title: ReactNode;
    description?: string;
    date: string;
    wasRead: boolean;
    onConfirm: () => void;
    onCancel?: () => void;
    withActions?: boolean;
    actionLabels?: { confirm: string; cancel: string };
  }[];
  onFilter(isRead: boolean): void;
  toggleNotification: VoidFunction;
  enableNotification: boolean;
  toggleAudio: VoidFunction;
  enableAudio: boolean;
  loadMore: VoidFunction;
  hasMore: boolean;
  isLoading: boolean;
  isRead?: boolean;
}

export default function NotificationsDropdownOverlay(props: Props) {
  const onScrollEnd: UIEventHandler<HTMLDivElement> = useCallback(
    ({ target }) => {
      const element = target as HTMLDivElement;

      if (
        element.scrollTop >=
        element.scrollHeight - element.clientHeight - 100
      ) {
        if (props.hasMore && !props.isLoading) {
          props.loadMore();
        }
      }
    },
    [props],
  );

  const markAllAsRead = useUserNotificationMarkAllAsRead();

  return (
    <div className="notifications-overlay-wrapper scroll">
      <Group position="between" style={{ padding: '0px 16px' }}>
        <span>Notificações</span>

        <Group gap={24}>
          <Tooltip
            title={
              props.enableNotification
                ? 'Desabilitar notificações'
                : 'Habilitar notificações'
            }
          >
            <ActionIcon onClick={props.toggleNotification}>
              <MessageSquare
                size={16}
                color={props.enableNotification ? 'black' : 'darkgray'}
              />
            </ActionIcon>
          </Tooltip>

          <Tooltip
            title={props.enableAudio ? 'Desabilitar som' : 'Habilitar som'}
          >
            <ActionIcon onClick={props.toggleAudio}>
              {props.enableAudio ? <Volume size={18} /> : <VolumeX size={18} />}
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      <div style={{ padding: '0.5rem 16px', fontWeight: 600 }}>
        <a
          href={`${process.env.REACT_APP_RELEASES_URL}/releases/sistema-ultracar`}
          target="_blank"
        >
          <Group gap={10}>
            <FileText size={16} />
            <span>Visualizar notas de atualizações</span>
          </Group>
        </a>
      </div>

      <Tabs
        defaultActiveKey="notifications"
        onChange={(key) => {
          if (key === 'unready-notify') {
            props.onFilter(false);
          } else {
            props.onFilter(true);
          }
        }}
      >
        <Tabs.TabPane key="unready-notify" tab="Não lidas" />
        <Tabs.TabPane key="read-notify" tab="Lidas" />
      </Tabs>

      <div className="notifications-overlay-body" onScroll={onScrollEnd}>
        <div style={{ padding: '0 16px' }}>
          <List
            dataSource={props?.dataSource}
            renderItem={(item) => (
              <List.Item
                className={`header-statistic-action ${
                  item.wasRead ? 'was-read' : ''
                }`}
                onClick={!item.withActions ? item.onConfirm : undefined}
                actions={
                  item.withActions
                    ? [
                        <a
                          key="ok"
                          onClick={(e) => {
                            e.preventDefault();
                            item.onConfirm();
                          }}
                        >
                          {item.actionLabels?.confirm || 'Confirmar'}
                        </a>,
                        !!item.onCancel && (
                          <a
                            key="cancel"
                            className="ant-list-item-action-cancel"
                            onClick={(e) => {
                              e.preventDefault();
                              item.onCancel?.();
                            }}
                          >
                            {item.actionLabels?.cancel || 'Cancelar'}
                          </a>
                        ),
                      ]
                    : undefined
                }
              >
                <List.Item.Meta
                  title={item.title}
                  description={item?.description}
                />

                <span style={{ color: '#d2d2d2' }}>{item?.date}</span>
              </List.Item>
            )}
          />

          {props.isLoading && (
            <span style={{ display: 'block', textAlign: 'center' }}>
              Carregando...
            </span>
          )}
        </div>
      </div>

      <div style={{ marginTop: 8, padding: '0px 26px' }}>
        {!!props?.dataSource?.length && !props.isRead && (
          <Button
            type="link"
            disabled={markAllAsRead.isLoading}
            onClick={async () => await markAllAsRead.mutateAsync()}
          >
            Marcar todas como lidas
          </Button>
        )}
      </div>
    </div>
  );
}
